import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useMonteurT, translateContentHeading } from "@/i18n/monteur";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { AccountPolicyBanner, usePolicyBanner } from "@/components/AccountPolicyBanner";
import { SafetySymbols } from "@/components/SafetySymbols";
import { VisualObjects } from "@/components/VisualObjects";
import { RichtigFalschRenderer } from "@/vsi/renderers";
import { buildRichtigFalsch } from "@/vsi/richtigFalsch";
import { setActiveLehrbildWorld, setLehrbildMonteurKontext } from "@/vsi/lehrbild";

// DEMO-SAFE-1: Anzeige-Reihenfolge der zusätzlichen Visual Learning Objects im
// Lernbereich. Sicherheitszeichen (safety_symbols) bleiben der bestehenden
// SafetySymbols-Leiste vorbehalten und werden hier bewusst NICHT erneut gerendert
// (keine Dopplung). Nur bereits vorhandene VLO-Typen, keine neuen Inhalte.
// IMPLEMENT-5 / S4: learning_focus NUR im Mitarbeiterflow aktiviert, didaktisch zuerst
// (nach der Sicherheitszeichen-Leiste, vor Gefahren-/PSA-Markern). Fail-closed: fehlt/
// leer -> der Renderer liefert null, der Eintrag entfällt.
const LEARNING_VLO_ORDER = { learning_focus: 1, hazard_markers: 2, psa_summary: 3 };

export default function MonteurAufgabe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useMonteurT();

  const [assignment, setAssignment] = useState(null);
  const [course, setCourse] = useState(null);

  // Schritt 0 = alle Inhalte auf einer Seite,
  // Schritt 1..N = die N Verständnisfragen.
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [wrongIndices, setWrongIndices] = useState([]); // Indizes falscher Fragen aus letztem Versuch
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [nachweisId, setNachweisId] = useState(null);
  const { policyError, handlePolicyError, clearPolicyError } = usePolicyBanner();

  useEffect(() => {
    api.getMyAssignment(id).then((a) => {
      setAssignment(a);
      setCourse(a.course);
    }).catch((err) => {
      if (err?.response?.status === 404) {
        toast.error(t("aufgabe.notFound"));
        navigate("/monteur/aufgaben", { replace: true });
      }
    });
  }, [id, navigate, t]);

  // IMPLEMENT-14: aktive Welt für das Lehrbild-Overlay setzen (lädt Bild-Manifest).
  useEffect(() => {
    if (course?.id) setActiveLehrbildWorld(course.id);
  }, [course?.id]);

  // IP2-D1C: Monteur-Renderkontext aktivieren (Mobile-Derivate bevorzugt,
  // Master-Fallback automatisch); beim Verlassen deterministisch zurücksetzen.
  useEffect(() => {
    setLehrbildMonteurKontext(true);
    return () => setLehrbildMonteurKontext(false);
  }, []);

  if (!assignment || !course) {
    return <div className="p-10 text-slate-500">{t("common.loading")}</div>;
  }

  const questionSteps = course.questions.length;
  const totalSteps = 1 + questionSteps; // 1 Inhaltsseite + Fragen
  const isContentStep = step === 0;
  const questionIndex = step - 1;
  const currentQuestion = !isContentStep ? course.questions[questionIndex] : null;
  const isLast = step === totalSteps - 1;
  const canNext = isContentStep || answers[questionIndex] !== undefined;
  const allAnswered = course.questions.every(
    (_, idx) => answers[idx] !== undefined
  );

  const handleSubmit = async () => {
    setSubmitting(true);
    clearPolicyError();
    try {
      const orderedAnswers = course.questions.map((_, idx) => answers[idx]);
      const res = await api.completeMyAssignment(id, orderedAnswers);
      if (res.status === "ok") {
        setNachweisId(res.nachweis_id);
        setDone(true);
        setWrongIndices([]);
      } else if (res.status === "wrong_answers") {
        const wrong = res.answers
          .map((a, i) => (a.correct ? null : i))
          .filter((i) => i !== null);
        setWrongIndices(wrong);
        // Falsche Antworten zurücksetzen, damit der Monteur sie neu wählen muss
        setAnswers((prev) => {
          const next = { ...prev };
          for (const i of wrong) delete next[i];
          return next;
        });
        // Direkt zur ersten falschen Frage springen
        if (wrong.length > 0) {
          setStep(1 + wrong[0]);
          toast.error(
            wrong.length === 1
              ? t("aufgabe.wrongOne")
              : t("aufgabe.wrongMany", { count: wrong.length })
          );
        }
      }
    } catch (e) {
      if (!handlePolicyError(e)) {
        toast.error(t("aufgabe.saveFailed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Fertig-Screen
  if (done) {
    return (
      <div
        className="min-h-screen bg-slate-50 flex items-center justify-center p-5"
        data-testid="monteur-done-screen"
      >
        <Card className="max-w-md w-full bg-white border border-slate-200 shadow-sm p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-9 h-9 text-emerald-700" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {t("aufgabe.doneTitle")}
          </h1>
          <p className="text-slate-600 mt-3">
            {t("aufgabe.doneBody", { title: course.title })}
          </p>
          <div className="mt-8 space-y-3">
            <Button
              onClick={() => navigate("/monteur/aufgaben")}
              className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-base"
              data-testid="monteur-back-to-tasks"
            >
              {t("aufgabe.backToTasks")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="monteur-aufgabe-page">
      {/* Header mit Fortschritt */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate("/monteur/aufgaben")}
              className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 text-sm"
              data-testid="monteur-aufgabe-back"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("aufgabe.backShort")}
            </button>
            <div className="text-xs text-slate-500 font-medium">
              {isContentStep
                ? t("aufgabe.stepContent")
                : t("aufgabe.stepQuestion", {
                    current: questionIndex + 1,
                    total: questionSteps,
                  })}
            </div>
          </div>
          <Progress
            value={((step + 1) / totalSteps) * 100}
            className="h-1.5"
            data-testid="monteur-progress"
          />
        </div>
      </header>

      <main className="max-w-lg mx-auto px-5 py-8">
        <div className="mb-2 text-xs uppercase tracking-widest text-slate-500 font-medium">
          {course.title}
        </div>

        {policyError && (
          <AccountPolicyBanner
            error={policyError}
            role="employee"
            onDismiss={clearPolicyError}
            className="mb-6"
          />
        )}

        {isContentStep ? (
          <Card
            className="bg-white border border-slate-200 shadow-sm p-6 sm:p-8"
            data-testid="content-page"
          >
            <h1 className="font-heading text-2xl font-bold text-slate-900 leading-tight">
              {t("aufgabe.readCalm")}
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              {t("aufgabe.questionsFollow", { count: questionSteps })}
            </p>

            <SafetySymbols symbols={course.safety_symbols} className="mt-6" />

            {(() => {
              // DEMO-SAFE-1: vorhandene VLOs zusätzlich sichtbar machen
              // (Gefahrenmarkierungen vor PSA-Zusammenfassung). Fail-closed:
              // fehlt/leer/fehlerhaft -> nichts anzeigen, kein Crash.
              const learningVlos = Array.isArray(course.visual_objects)
                ? course.visual_objects
                    .filter((o) => o && LEARNING_VLO_ORDER[o.vlo_type] != null)
                    .sort(
                      (a, b) =>
                        LEARNING_VLO_ORDER[a.vlo_type] - LEARNING_VLO_ORDER[b.vlo_type]
                    )
                : [];
              if (learningVlos.length === 0) return null;
              return (
                <div className="mt-6 space-y-4" data-testid="monteur-visual-objects">
                  <VisualObjects
                    objects={learningVlos}
                    sourceObjects={
                      course.source_display && typeof course.source_display === "object"
                        ? course.source_display.visual_objects || null
                        : null
                    }
                  />
                </div>
              );
            })()}

            {/* IMPLEMENT-7 / VLO-RICHTIG-FALSCH: erster visueller Lernbaustein im
                Mitarbeiterflow. Rein frontendseitig aus vorhandenen Betriebsanweisungs-
                Texten (Schutzmaßnahmen=Richtig, Hauptgefahren=Falsch). Fail-closed:
                ohne Texte -> null, der Block entfällt. */}
            {(() => {
              const rf = buildRichtigFalsch(course);
              if (!rf) return null;
              // Guard: natives Course-Feld `source` (String "ai_generated") ignorieren.
              const rfSource =
                course.source_display && typeof course.source_display === "object"
                  ? buildRichtigFalsch(course.source_display)
                  : null;
              return (
                <div className="mt-6" data-testid="monteur-richtig-falsch">
                  <RichtigFalschRenderer vlo={rf} sourceVlo={rfSource} />
                </div>
              );
            })()}

            <div className="mt-8 space-y-8">
              {course.content.map((block, idx) => (
                <section
                  key={idx}
                  data-testid={`content-section-${idx}`}
                  className="border-l-2 border-slate-900 pl-5"
                >
                  <h2 className="font-heading text-lg font-bold text-slate-900">
                    {translateContentHeading(t, block.heading)}
                  </h2>
                  <p className="mt-2 text-slate-700 leading-relaxed text-[15px] whitespace-pre-line">
                    {block.body}
                  </p>
                </section>
              ))}
            </div>
          </Card>
        ) : (
          <Card
            className="bg-white border border-slate-200 shadow-sm p-6 sm:p-8"
            data-testid={`question-step-${questionIndex}`}
          >
            <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">
              {t("aufgabe.questionLabel", {
                current: questionIndex + 1,
                total: questionSteps,
              })}
            </div>
            <h2 className="font-heading text-xl font-bold text-slate-900 mt-2 leading-snug">
              {currentQuestion.text}
            </h2>

            {wrongIndices.includes(questionIndex) && (
              <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2 text-sm text-amber-900">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {t("aufgabe.wrongAgain")}
                </span>
              </div>
            )}

            <div className="mt-6 space-y-2.5">
              {currentQuestion.options.map((opt, optIdx) => {
                const selected = answers[questionIndex] === optIdx;
                return (
                  <button
                    key={optIdx}
                    onClick={() =>
                      setAnswers({ ...answers, [questionIndex]: optIdx })
                    }
                    data-testid={`question-${questionIndex}-option-${optIdx}`}
                    className={`w-full text-left p-4 rounded-md border-2 transition-all ${
                      selected
                        ? "border-slate-900 bg-slate-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          selected
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-300"
                        }`}
                      >
                        {selected && <Check className="w-3.5 h-3.5" />}
                      </div>
                      <span
                        className={
                          selected
                            ? "text-slate-900 font-medium"
                            : "text-slate-700"
                        }
                      >
                        {opt}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* Navigation */}
        <div className="mt-6 flex gap-3">
          {step > 0 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-14 text-base"
              data-testid="monteur-prev-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("aufgabe.prev")}
            </Button>
          )}
          {isLast ? (
            <Button
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="flex-1 h-14 text-base bg-slate-900 hover:bg-slate-800"
              data-testid="monteur-submit-button"
            >
              {submitting ? t("aufgabe.saving") : t("aufgabe.finish")}
              <Check className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canNext}
              className="flex-1 h-14 text-base bg-slate-900 hover:bg-slate-800"
              data-testid="monteur-next-button"
            >
              {isContentStep ? t("aufgabe.toQuestions") : t("aufgabe.next")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
